import { FactoryProvider, InjectionToken } from '@angular/core';
import {
  getRmaApiUrl,
  getRmaBaseUrl,
  getRmaBillingApiUrl,
  getRmaCdnUrl,
  getRmaCrmApiUrl,
  getRmaDashboardUrl,
  getRmaEmailApiUrl,
  getRmaFindMyApiUrl,
  getRmaIdentityUrl,
  getRmaImagesUrl,
  getRmaLandingPagesUrl,
  getRmaLanguageUrl,
  getRmaMarketorApiUrl,
  getRmaPromoterUrl,
  getRmaReviewsApiUrl,
  getRmaSocialApiUrl,
  getRmaStaticImageUrl,
  getRmaStaticUrl,
  getRmaSupportUrl,
  getRmaSurveyApiUrl,
  getRmaTrixelsApiUrl,
  getRmaWidgetsApiUrl,
  getSocialStudioUrl,
} from '@rma/generic/core/urls';
import { RmaEnvironment, RmaTenant } from '@rma/generic/ng/instance';
import {
  environmentTenantedUrlFactory,
  environmentUrlFactory,
  globalUrlFactory,
  RmaUrlFactory,
  RmaUrlOverride,
  tenantedUrlFactory,
} from './rma-url.factory';

// API URLs (require both tenant and environment)
export const appendToRmaApiUrl = new InjectionToken<RmaUrlFactory>('appendToRmaApiUrl');
export const appendToRmaBaseUrl = new InjectionToken<RmaUrlFactory>('appendToRmaBaseUrl');
export const appendToRmaBillingApiUrl = new InjectionToken<RmaUrlFactory>('appendToRmaBillingApiUrl');
export const appendToRmaCrmApiUrl = new InjectionToken<RmaUrlFactory>('appendToRmaCrmApiUrl');
export const appendToRmaEmailApiUrl = new InjectionToken<RmaUrlFactory>('appendToRmaEmailApiUrl');
export const appendToRmaFindMyApiUrl = new InjectionToken<RmaUrlFactory>('appendToRmaFindMyApiUrl');
export const appendToRmaReviewsApiUrl = new InjectionToken<RmaUrlFactory>('appendToRmaReviewsApiUrl');
export const appendToRmaSocialApiUrl = new InjectionToken<RmaUrlFactory>('appendToRmaSocialApiUrl');
export const appendToRmaSurveyApiUrl = new InjectionToken<RmaUrlFactory>('appendToRmaSurveyApiUrl');
export const appendToRmaLandingPagesUrl = new InjectionToken<RmaUrlFactory>('appendToRmaLandingPagesUrl');
export const appendToRmaDashboardUrl = new InjectionToken<RmaUrlFactory>('appendToRmaDashboardUrl');
export const appendToRmaPromoterUrl = new InjectionToken<RmaUrlFactory>('appendToRmaPromoterUrl');

// Environment-only URLs (require environment but not tenant)
export const appendToRmaWidgetsApiUrl = new InjectionToken<RmaUrlFactory>('appendToRmaWidgetsApiUrl');
export const appendToRmaTrixelsApiUrl = new InjectionToken<RmaUrlFactory>('appendToRmaTrixelsApiUrl');
export const appendToRmaIdentityUrl = new InjectionToken<RmaUrlFactory>('appendToRmaIdentityUrl');

// Static URLs (require tenant but not environment)
export const appendToRmaStaticUrl = new InjectionToken<RmaUrlFactory>('appendToRmaStaticUrl');
export const appendToRmaStaticImageUrl = new InjectionToken<RmaUrlFactory>('appendToRmaStaticImageUrl');
export const appendToRmaCdnUrl = new InjectionToken<RmaUrlFactory>('appendToRmaCdnUrl');

// Fixed URLs (require neither tenant nor environment)
export const appendToRmaMarketorApiUrl = new InjectionToken<RmaUrlFactory>('appendToRmaMarketorApiUrl');
export const appendToRmaSupportUrl = new InjectionToken<RmaUrlFactory>('appendToRmaSupportUrl');
export const appendToRmaLanguageUrl = new InjectionToken<RmaUrlFactory>('appendToRmaLanguageUrl');
export const appendToRmaImagesUrl = new InjectionToken<RmaUrlFactory>('appendToRmaImagesUrl');
export const appendToSocialStudioUrl = new InjectionToken<RmaUrlFactory>('appendToSocialStudioUrl');

// API URLs Providers (tenant + environment)
export function provideRmaApiUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaApiUrl,
    useFactory: environmentTenantedUrlFactory(getRmaApiUrl, override),
    deps: [RmaTenant, RmaEnvironment],
  };
}

export function provideRmaBaseUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaBaseUrl,
    useFactory: environmentTenantedUrlFactory(getRmaBaseUrl, override),
    deps: [RmaTenant, RmaEnvironment],
  };
}

export function provideRmaBillingApiUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaBillingApiUrl,
    useFactory: environmentTenantedUrlFactory(getRmaBillingApiUrl, override),
    deps: [RmaTenant, RmaEnvironment],
  };
}

export function provideRmaCrmApiUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaCrmApiUrl,
    useFactory: environmentTenantedUrlFactory(getRmaCrmApiUrl, override),
    deps: [RmaTenant, RmaEnvironment],
  };
}

export function provideRmaEmailApiUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaEmailApiUrl,
    useFactory: environmentTenantedUrlFactory(getRmaEmailApiUrl, override),
    deps: [RmaTenant, RmaEnvironment],
  };
}

export function provideRmaFindMyApiUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaFindMyApiUrl,
    useFactory: environmentTenantedUrlFactory(getRmaFindMyApiUrl, override),
    deps: [RmaTenant, RmaEnvironment],
  };
}

export function provideRmaReviewsApiUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaReviewsApiUrl,
    useFactory: environmentTenantedUrlFactory(getRmaReviewsApiUrl, override),
    deps: [RmaTenant, RmaEnvironment],
  };
}

export function provideRmaSocialApiUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaSocialApiUrl,
    useFactory: environmentTenantedUrlFactory(getRmaSocialApiUrl, override),
    deps: [RmaTenant, RmaEnvironment],
  };
}

export function provideRmaSurveyApiUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaSurveyApiUrl,
    useFactory: environmentUrlFactory(getRmaSurveyApiUrl, override),
    deps: [RmaEnvironment],
  };
}

export function provideRmaLandingPagesUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaLandingPagesUrl,
    useFactory: environmentTenantedUrlFactory(getRmaLandingPagesUrl, override),
    deps: [RmaTenant, RmaEnvironment],
  };
}

export function provideRmaDashboardUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaDashboardUrl,
    useFactory: environmentTenantedUrlFactory(getRmaDashboardUrl, override),
    deps: [RmaTenant, RmaEnvironment],
  };
}

export function provideRmaPromoterUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaPromoterUrl,
    useFactory: environmentTenantedUrlFactory(getRmaPromoterUrl, override),
    deps: [RmaTenant, RmaEnvironment],
  };
}

// Environment-only URL Providers
export function provideRmaWidgetsApiUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaWidgetsApiUrl,
    useFactory: environmentUrlFactory(getRmaWidgetsApiUrl, override),
    deps: [RmaEnvironment],
  };
}

export function provideRmaTrixelsApiUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaTrixelsApiUrl,
    useFactory: environmentUrlFactory(getRmaTrixelsApiUrl, override),
    deps: [RmaEnvironment],
  };
}

export function provideRmaIdentityUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaIdentityUrl,
    useFactory: environmentUrlFactory(getRmaIdentityUrl, override),
    deps: [RmaEnvironment],
  };
}

// Tenant-only URL Providers
export function provideRmaStaticUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaStaticUrl,
    useFactory: environmentTenantedUrlFactory(getRmaStaticUrl, override),
    deps: [RmaTenant, RmaEnvironment],
  };
}

export function provideRmaStaticImageUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaStaticImageUrl,
    useFactory: environmentTenantedUrlFactory(getRmaStaticImageUrl, override),
    deps: [RmaTenant, RmaEnvironment],
  };
}

export function provideRmaCdnUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaCdnUrl,
    useFactory: tenantedUrlFactory(getRmaCdnUrl, override),
    deps: [RmaTenant],
  };
}

// Fixed URL Providers
export function provideRmaMarketorApiUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaMarketorApiUrl,
    useFactory: environmentUrlFactory(getRmaMarketorApiUrl, override),
    deps: [RmaEnvironment],
  };
}

export function provideRmaSupportUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaSupportUrl,
    useFactory: globalUrlFactory(getRmaSupportUrl, override),
  };
}

export function provideRmaLanguageUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaLanguageUrl,
    useFactory: globalUrlFactory(getRmaLanguageUrl, override),
  };
}

export function provideRmaImagesUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToRmaImagesUrl,
    useFactory: tenantedUrlFactory(getRmaImagesUrl, override),
    deps: [RmaTenant],
  };
}

export function provideSocialStudioUrl(override?: RmaUrlOverride): FactoryProvider {
  return {
    provide: appendToSocialStudioUrl,
    useFactory: environmentTenantedUrlFactory(getSocialStudioUrl, override),
    deps: [RmaTenant, RmaEnvironment],
  };
}
